import { Prisma, prisma, SeatOverrideStatus, SeatNumberingDirection } from "client";
import type { SeatOverride, SeatSectionConfig } from "client";
import { isSeatWithinConfig } from "./seat-section-rules";

/**
 * The ONLY way to claim a derived seat.
 *
 * There is no row to lock on MongoDB and no seat document to read, so
 * `findFirst` then `create` is a race: two buyers both see "free" and both
 * write. Instead the @@unique([seatSectionId, row, seat]) index IS the mutex —
 * we create unconditionally and treat P2002 as "someone else got it".
 *
 * Every seat claim must go through this function. A second write path
 * reintroduces the race no matter how careful it looks.
 */

export type SeatRef = {
  seatSectionId: string;
  row: number;
  seat: number;
};

export type ClaimSeatInput = SeatRef & {
  status: Extract<
    SeatOverrideStatus,
    "ON_HOLD" | "RESERVED" | "BLOCKED" | "REMOVED"
  >;
  orderItemId?: string | null;
  label?: string | null;
  /** Minor units. */
  price?: number | null;
  expiresAt?: Date | null;
  metaData?: Prisma.InputJsonValue;
};

export type ClaimSeatResult =
  | { ok: true; override: SeatOverride }
  | { ok: false; reason: "taken" };

const isUniqueViolation = (error: unknown): boolean =>
  error instanceof Prisma.PrismaClientKnownRequestError &&
  error.code === "P2002";

export const claimSeat = async (
  input: ClaimSeatInput,
  client: Pick<typeof prisma, "seatOverride"> = prisma
): Promise<ClaimSeatResult> => {
  try {
    const override = await client.seatOverride.create({
      data: {
        seatSectionId: input.seatSectionId,
        row: input.row,
        seat: input.seat,
        status: input.status,
        orderItemId: input.orderItemId ?? null,
        label: input.label ?? null,
        price: input.price ?? null,
        expiresAt: input.expiresAt ?? null,
        ...(input.metaData !== undefined ? { metaData: input.metaData } : {}),
      },
    });
    return { ok: true, override };
  } catch (error) {
    if (!isUniqueViolation(error)) throw error;

    // Label/hideLabel-only rows use status AVAILABLE (decoration). A real claim
    // may overwrite those; any other status means the seat is taken.
    const existing = await client.seatOverride.findUnique({
      where: {
        seatSectionId_row_seat: {
          seatSectionId: input.seatSectionId,
          row: input.row,
          seat: input.seat,
        },
      },
    });
    if (!existing || existing.status !== SeatOverrideStatus.AVAILABLE) {
      return { ok: false, reason: "taken" };
    }

    const override = await client.seatOverride.update({
      where: { id: existing.id },
      data: {
        status: input.status,
        orderItemId: input.orderItemId ?? null,
        label: input.label ?? existing.label,
        price: input.price ?? null,
        expiresAt: input.expiresAt ?? null,
        ...(input.metaData !== undefined ? { metaData: input.metaData } : {}),
      },
    });
    return { ok: true, override };
  }
};

/**
 * Release seats back to available by DELETING the rows.
 * Delta storage only stays small if "available" is the absence of a row, so
 * releasing must never write status = AVAILABLE.
 */
export const releaseSeatsByOrderItemIds = async (
  orderItemIds: string[],
  client: Pick<typeof prisma, "seatOverride"> = prisma
): Promise<number> => {
  if (orderItemIds.length === 0) return 0;
  const { count } = await client.seatOverride.deleteMany({
    where: { orderItemId: { in: orderItemIds } },
  });
  return count;
};

export const releaseSeats = async (
  seats: SeatRef[],
  client: Pick<typeof prisma, "seatOverride"> = prisma
): Promise<number> => {
  if (seats.length === 0) return 0;
  const { count } = await client.seatOverride.deleteMany({
    where: { OR: seats.map((seat) => ({ ...seat })) },
  });
  return count;
};

/** Confirm a hold after payment: keep the row, drop the expiry. */
export const confirmSeatsByOrderItemIds = async (
  orderItemIds: string[],
  client: Pick<typeof prisma, "seatOverride"> = prisma
): Promise<number> => {
  if (orderItemIds.length === 0) return 0;
  const { count } = await client.seatOverride.updateMany({
    where: { orderItemId: { in: orderItemIds } },
    data: { status: SeatOverrideStatus.RESERVED, expiresAt: null },
  });
  return count;
};

/**
 * Belt-and-braces sweep for holds whose order died without releasing them.
 * One indexed query thanks to @@index([status, expiresAt]) — this is exactly why
 * expiresAt is denormalised onto the override instead of joined from the order.
 */
export const sweepExpiredSeatHolds = async (
  now: Date = new Date()
): Promise<number> => {
  const { count } = await prisma.seatOverride.deleteMany({
    where: { status: SeatOverrideStatus.ON_HOLD, expiresAt: { lt: now } },
  });
  return count;
};

/** Non-default seats for a set of sections — the viewer availability payload. */
export const getSeatOverridesForSections = async (
  seatSectionIds: string[]
) => {
  if (seatSectionIds.length === 0) return [];
  const rows = await prisma.seatOverride.findMany({
    where: { seatSectionId: { in: seatSectionIds } },
    select: {
      seatSectionId: true,
      row: true,
      seat: true,
      status: true,
      label: true,
      price: true,
      metaData: true,
    },
  });
  return rows.map((row) => {
    const meta =
      row.metaData && typeof row.metaData === "object" && !Array.isArray(row.metaData)
        ? (row.metaData as Record<string, unknown>)
        : null;
    return {
      seatSectionId: row.seatSectionId,
      row: row.row,
      seat: row.seat,
      status: row.status,
      label: row.label,
      price: row.price,
      hideLabel: meta?.hideLabel === true,
    };
  });
};

export type UpsertSeatDecorationInput = {
  seatSectionId: string;
  row: number;
  seat: number;
  label?: string | null;
  hideLabel?: boolean;
  blocked?: boolean;
  /** When provided, used for default-label collision checks within the row. */
  seatSection?: SeatSectionConfig | null;
};

const defaultSeatLabel = (
  seatIndex: number,
  seatCount: number,
  direction: SeatNumberingDirection | string | undefined,
  firstSeatNumber: number
): string => {
  if (seatIndex < 0 || seatCount <= 0) return "";
  if (direction === SeatNumberingDirection.RIGHT_TO_LEFT) {
    return String(seatCount - seatIndex - 1 + firstSeatNumber);
  }
  return String(seatIndex + firstSeatNumber);
};

/**
 * Reject labels that collide with another seat's effective label in the same row.
 */
const assertUniqueSeatLabelInRow = async (
  input: UpsertSeatDecorationInput,
  label: string,
  client: typeof prisma
) => {
  const normalized = label.trim();
  if (!normalized) return;

  const config = input.seatSection;
  if (!config) return;

  const rowCfg = config.rowsConfig?.find((r) => r.rowIndex === input.row);
  const seatCount =
    rowCfg?.seatCount ??
    (isSeatWithinConfig(config, input.row, 0) ? config.seatsPerRow : 0);
  if (seatCount <= 0) return;

  const siblings = await client.seatOverride.findMany({
    where: {
      seatSectionId: input.seatSectionId,
      row: input.row,
      NOT: { seat: input.seat },
    },
    select: { seat: true, label: true },
  });
  const bySeat = new Map(siblings.map((s) => [s.seat, s.label]));

  for (let s = 0; s < seatCount; s++) {
    if (s === input.seat) continue;
    if ((rowCfg?.removedSeats ?? []).includes(s)) continue;
    const siblingLabel =
      bySeat.get(s)?.trim() ||
      defaultSeatLabel(
        s,
        seatCount,
        config.seatNumberingDirection,
        config.firstSeatNumber
      );
    if (siblingLabel === normalized) {
      throw new Error("Seat label must be unique within this row");
    }
  }
};

/**
 * Admin decoration / block for a derived seat.
 * - blocked=true → BLOCKED
 * - blocked=false + no label/hideLabel → delete row (back to default available)
 * - otherwise upsert AVAILABLE (or keep BLOCKED) with label/hideLabel in metaData
 */
export const upsertSeatDecoration = async (
  input: UpsertSeatDecorationInput,
  client: typeof prisma = prisma
) => {
  const existing = await client.seatOverride.findUnique({
    where: {
      seatSectionId_row_seat: {
        seatSectionId: input.seatSectionId,
        row: input.row,
        seat: input.seat,
      },
    },
  });

  const hideLabel = input.hideLabel === true;
  const label =
    input.label === undefined ? existing?.label ?? null : input.label;
  const blocked =
    input.blocked === true ||
    (input.blocked !== false &&
      existing?.status === SeatOverrideStatus.BLOCKED);

  if (typeof label === "string" && label.trim()) {
    await assertUniqueSeatLabelInRow(input, label, client);
  }

  // Unblock + clear decoration → absence
  if (
    input.blocked === false &&
    !hideLabel &&
    (label === null || label === "")
  ) {
    if (existing) {
      await client.seatOverride.delete({ where: { id: existing.id } });
    }
    return {
      seatSectionId: input.seatSectionId,
      row: input.row,
      seat: input.seat,
      status: SeatOverrideStatus.AVAILABLE,
      label: null,
      hideLabel: false,
    };
  }

  // Don't clobber holds / sales with decoration edits
  if (
    existing &&
    existing.status !== SeatOverrideStatus.AVAILABLE &&
    existing.status !== SeatOverrideStatus.BLOCKED
  ) {
    const meta = {
      ...((existing.metaData &&
      typeof existing.metaData === "object" &&
      !Array.isArray(existing.metaData)
        ? existing.metaData
        : {}) as Record<string, unknown>),
      hideLabel,
    };
    const updated = await client.seatOverride.update({
      where: { id: existing.id },
      data: {
        label,
        metaData: meta as Prisma.InputJsonValue,
      },
    });
    return {
      seatSectionId: updated.seatSectionId,
      row: updated.row,
      seat: updated.seat,
      status: updated.status,
      label: updated.label,
      hideLabel,
    };
  }

  const status = blocked
    ? SeatOverrideStatus.BLOCKED
    : SeatOverrideStatus.AVAILABLE;
  const metaData = { hideLabel };

  if (existing) {
    const updated = await client.seatOverride.update({
      where: { id: existing.id },
      data: { status, label, metaData, orderItemId: null, expiresAt: null },
    });
    return {
      seatSectionId: updated.seatSectionId,
      row: updated.row,
      seat: updated.seat,
      status: updated.status,
      label: updated.label,
      hideLabel,
    };
  }

  const created = await client.seatOverride.create({
    data: {
      seatSectionId: input.seatSectionId,
      row: input.row,
      seat: input.seat,
      status,
      label,
      metaData,
    },
  });
  return {
    seatSectionId: created.seatSectionId,
    row: created.row,
    seat: created.seat,
    status: created.status,
    label: created.label,
    hideLabel,
  };
};
